"use client"

import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { X } from 'lucide-react'
import { cn } from '../../lib/utils'
import { SIDEBAR_WIDTH, SIDEBAR_WIDTH_ICON } from '../../components/sidebar'
import { UIButton } from '../buttons/ui-button'
import { UIBox } from './ui-box'

// Optional import - useSidebar throws if outside SidebarProvider
let _useSidebar: (() => { open: boolean; isMobile: boolean }) | undefined
try {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const mod = require('../../components/sidebar')
  _useSidebar = mod.useSidebar
} catch {
  // Not available
}

const maxWidthPresets = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  full: 'max-w-full',
} as const

const bottomOffsetPresets = {
  sm: 'bottom-4',
  md: 'bottom-8',
  lg: 'bottom-12',
  xl: 'bottom-16',
} as const

export interface UIActionBarProps {
  /** Whether the bar is visible */
  open: boolean
  /** Close (X) button click handler */
  onClose: () => void
  /** Left-side label content (displayed right of X button) */
  label: React.ReactNode
  /** Right-side action buttons */
  children: React.ReactNode
  /** Auto-calculate sidebar offset (default: true). Set false for Storybook or non-sidebar contexts */
  sidebarAware?: boolean
  /** Maximum width preset - caps the bar width. Bar auto-sizes to content. (default: "3xl") */
  maxWidth?: keyof typeof maxWidthPresets
  /** Bottom offset preset (default: "lg" = bottom-12) */
  bottomOffset?: keyof typeof bottomOffsetPresets
  /** Disable portal rendering - renders inline with sticky positioning. Use in Storybook or isolated contexts. */
  disablePortal?: boolean
  /** Additional className for the outer container */
  className?: string
}

/**
 * UIActionBar - 하단 고정 일괄 작업 바 (Adobe Spectrum 패턴)
 *
 * Portal을 사용하여 스크롤 컨테이너를 탈출하고, 사이드바 오프셋을 자동 계산합니다.
 * SidebarProvider 내부에서 sidebarAware=true(기본값)로 사용하면
 * 사이드바 열림/닫힘에 따라 위치가 자동 조정됩니다.
 *
 * Storybook이나 iframe 컨텍스트에서는 disablePortal=true를 사용하면
 * portal 없이 inline으로 렌더링됩니다.
 *
 * @example
 * <UIActionBar
 *   open={selectedItems.length > 0}
 *   onClose={() => setSelectedItems([])}
 *   label={<UIBadge>{selectedItems.length} selected</UIBadge>}
 * >
 *   <UIButton onClick={handleDownload}>Download</UIButton>
 *   <UIButton variant="destructive" onClick={handleDelete}>Delete</UIButton>
 * </UIActionBar>
 */
export const UIActionBar = React.forwardRef<HTMLDivElement, UIActionBarProps>(
  ({ open, onClose, label, children, sidebarAware = true, maxWidth = '3xl', bottomOffset = 'lg', disablePortal = false, className }, ref) => {
    const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null)

    // Safely access sidebar context (may throw outside SidebarProvider)
    let sidebarOpen = false
    let isMobile = false

    if (sidebarAware && !disablePortal && _useSidebar) {
      try {
        const sidebar = _useSidebar()
        sidebarOpen = sidebar.open
        isMobile = sidebar.isMobile
      } catch {
        // Outside SidebarProvider - fall back to no offset
      }
    }

    useEffect(() => {
      if (!disablePortal) setPortalContainer(document.body)
    }, [disablePortal])

    const leftOffset = !sidebarAware || isMobile || disablePortal
      ? '0.75rem'
      : sidebarOpen
        ? `calc(${SIDEBAR_WIDTH} + 0.75rem)`
        : `calc(${SIDEBAR_WIDTH_ICON} + 0.75rem)`

    // Inner bar content (shared between portal and inline modes)
    const barContent = (
      <AnimatePresence>
        {open && (
          <motion.div
            ref={ref}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 12 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className={cn(
              disablePortal
                ? "sticky bottom-0 z-50 flex justify-center py-4"
                : "fixed z-50 flex justify-center",
              !disablePortal && bottomOffsetPresets[bottomOffset],
              !disablePortal && "transition-[left,right] duration-200 ease-linear",
              className,
            )}
            style={disablePortal ? undefined : { left: leftOffset, right: '0.75rem' }}
          >
            <UIBox className={cn(
              "flex items-center gap-3 px-3 py-2",
              "bg-background/95 border rounded-xl shadow-lg",
              maxWidthPresets[maxWidth],
            )}>
              <UIButton
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="h-8 w-8 flex-shrink-0"
              >
                <X className="h-4 w-4" />
              </UIButton>
              {label}
              <UIBox className="flex items-center gap-2">
                {children}
              </UIBox>
            </UIBox>
          </motion.div>
        )}
      </AnimatePresence>
    )

    // Inline mode (Storybook, iframe contexts)
    if (disablePortal) return barContent

    // Portal mode (production - escapes scroll containers)
    if (!portalContainer) return null
    return createPortal(barContent, portalContainer)
  }
)

UIActionBar.displayName = "UIActionBar"
